import { useState, useRef, useEffect } from "react";
import { useT } from "@/i18n/LanguageContext";

export default function LanguageSwitcher({ tone = "ink" }) {
  const { locale, setLocale, locales, t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [open]);

  const current = locales.find((l) => l.code === locale) || locales[0];
  const colorBase = tone === "bone" ? "text-bone" : "text-ink";
  const colorMuted = tone === "bone" ? "text-bone/60" : "text-ink/60";
  const bg = tone === "bone" ? "bg-ink border-bone/20" : "bg-bone border-ink/15";

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t("nav.language")}
        data-testid="language-switcher-toggle"
        data-cursor="hover"
        className={`font-mono text-[11px] tracking-[0.3em] uppercase link-underline ${colorBase}`}
      >
        {current.label}
        <span className={`ml-1 ${colorMuted}`}>↓</span>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t("nav.language")}
          data-testid="language-switcher-menu"
          className={`absolute right-0 mt-3 min-w-[140px] border ${bg} z-[9000] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.45)]`}
        >
          {locales.map((l) => {
            const active = l.code === locale;
            return (
              <li key={l.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  data-testid={`language-${l.code}`}
                  onClick={() => {
                    setLocale(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 font-mono text-[11px] tracking-[0.25em] uppercase text-left transition-colors ${
                    active
                      ? "text-crimson"
                      : tone === "bone"
                      ? "text-bone hover:text-crimson"
                      : "text-ink hover:text-crimson"
                  }`}
                >
                  <span>{l.label}</span>
                  <span className={colorMuted}>{l.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
